import type { ReactNode } from 'react';

/**
 * The icon set.
 *
 * Drawn for this product rather than imported: every glyph is authored on the
 * same 24×24 grid with a 1.75 stroke, round caps and round joins, so the set
 * reads as one hand. That consistency is the reason not to pull in a general
 * library — mixing a stroke library's optical weight with a product's own marks
 * is the fastest way to make an interface look assembled.
 *
 * Icons are inline SVG, so they inherit `currentColor` and the page's text
 * size, cost no extra request, and cannot go missing at runtime. There are no
 * raster assets anywhere in the bundle.
 */

/** Every glyph the system can draw. */
export type IconName =
  | 'alert'
  | 'arrow-down'
  | 'arrow-up'
  | 'assistant'
  | 'audit'
  | 'calendar'
  | 'check'
  | 'chevron-down'
  | 'chevron-left'
  | 'chevron-right'
  | 'chevron-up'
  | 'clock'
  | 'close'
  | 'customers'
  | 'dispatch'
  | 'equipment'
  | 'field'
  | 'filter'
  | 'info'
  | 'inventory'
  | 'invoicing'
  | 'jobs'
  | 'leads'
  | 'link'
  | 'lock'
  | 'menu'
  | 'payroll'
  | 'pencil'
  | 'plus'
  | 'merge'
  | 'upload'
  | 'pipeline'
  | 'pricebook'
  | 'properties'
  | 'proposals'
  | 'reports'
  | 'map'
  | 'search'
  | 'settings'
  | 'sign-out'
  | 'sun'
  | 'moon'
  | 'overview'
  | 'system-theme'
  | 'team'
  | 'trash'
  | 'user';

const GLYPHS: Record<IconName, ReactNode> = {
  alert: (
    <>
      <path d="M12 4.5 21 19.5H3Z" />
      <path d="M12 10v4.2" />
      <path d="M12 17.3h.01" />
    </>
  ),
  'arrow-down': (
    <>
      <path d="M12 5v14" />
      <path d="M18 13l-6 6-6-6" />
    </>
  ),
  'arrow-up': (
    <>
      <path d="M12 19V5" />
      <path d="M6 11l6-6 6 6" />
    </>
  ),
  assistant: (
    <>
      <path d="M11 3.5 12.6 8.9 18 10.5 12.6 12.1 11 17.5 9.4 12.1 4 10.5 9.4 8.9Z" />
      <path d="M17.5 15.5l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8Z" />
    </>
  ),
  audit: (
    <>
      <path d="M12 3.5 19.5 6.5v5.4c0 4.4-3.1 7.5-7.5 8.6-4.4-1.1-7.5-4.2-7.5-8.6V6.5Z" />
      <path d="M9 12.1l2.2 2.2L15.5 10" />
    </>
  ),
  calendar: (
    <>
      <rect x="3.5" y="5" width="17" height="15.5" rx="1.5" />
      <path d="M3.5 9.5h17" />
      <path d="M8 3.5v3.5" />
      <path d="M16 3.5v3.5" />
    </>
  ),
  check: <path d="M4.5 12.5 9.5 17.5 19.5 6.5" />,
  'chevron-down': <path d="M6 9.5 12 15.5 18 9.5" />,
  'chevron-left': <path d="M14.5 6 8.5 12 14.5 18" />,
  'chevron-right': <path d="M9.5 6 15.5 12 9.5 18" />,
  'chevron-up': <path d="M6 14.5 12 8.5 18 14.5" />,
  clock: (
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 7.3V12l3.2 2" />
    </>
  ),
  close: (
    <>
      <path d="M6 6 18 18" />
      <path d="M18 6 6 18" />
    </>
  ),
  customers: (
    <>
      <circle cx="9.5" cy="8.5" r="3.3" />
      <path d="M3.5 20a6 6 0 0 1 12 0" />
      <path d="M16.2 5.6a3.3 3.3 0 0 1 0 5.9" />
      <path d="M17.2 14.5a6 6 0 0 1 3.3 5.5" />
    </>
  ),
  dispatch: (
    <>
      <rect x="3.5" y="4.5" width="17" height="15" rx="1.5" />
      <path d="M3.5 9h17" />
      <path d="M9.2 9v10.5" />
      <path d="M14.8 9v10.5" />
    </>
  ),
  equipment: (
    <>
      <rect x="4" y="4.5" width="16" height="15" rx="1.5" />
      <path d="M4 9h16" />
      <circle cx="12" cy="14.2" r="3" />
    </>
  ),
  field: (
    <>
      <rect x="7" y="3" width="10" height="18" rx="2" />
      <path d="M10.5 17.8h3" />
    </>
  ),
  filter: (
    <>
      <path d="M4 6.5h16" />
      <path d="M7 12h10" />
      <path d="M10 17.5h4" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 11.2v5.3" />
      <path d="M12 7.9h.01" />
    </>
  ),
  inventory: (
    <>
      <path d="M12 3.5 20 7.5v9L12 20.5 4 16.5v-9Z" />
      <path d="M4 7.5 12 11.5 20 7.5" />
      <path d="M12 11.5V20.5" />
    </>
  ),
  invoicing: (
    <>
      <path d="M6 3.5h12v17l-3-2-3 2-3-2-3 2Z" />
      <path d="M9 8.5h6" />
      <path d="M9 12.5h6" />
    </>
  ),
  jobs: (
    <>
      <rect x="4.5" y="5.5" width="15" height="14.5" rx="1.5" />
      <rect x="9" y="3" width="6" height="3.6" rx="1" />
      <path d="M8.5 11.5h7" />
      <path d="M8.5 15.2h4.5" />
    </>
  ),
  leads: (
    <>
      <path d="M3.5 13.5 6 5.5h12l2.5 8" />
      <path d="M3.5 13.5h4l1.2 2.6h6.6l1.2-2.6h4" />
      <path d="M3.5 13.5V19a1.5 1.5 0 0 0 1.5 1.5h14a1.5 1.5 0 0 0 1.5-1.5v-5.5" />
    </>
  ),
  link: (
    <>
      <path d="M10.2 14a4.2 4.2 0 0 0 5.9 0l2.6-2.6a4.2 4.2 0 1 0-5.9-5.9l-1.3 1.3" />
      <path d="M13.8 10a4.2 4.2 0 0 0-5.9 0l-2.6 2.6a4.2 4.2 0 1 0 5.9 5.9l1.3-1.3" />
    </>
  ),
  lock: (
    <>
      <rect x="5" y="10.5" width="14" height="9.5" rx="1.5" />
      <path d="M8.5 10.5V8a3.5 3.5 0 0 1 7 0v2.5" />
    </>
  ),
  map: (
    <>
      <path d="M9 4.5 3.5 6.8v12.7L9 17.2l6 2.3 5.5-2.3V4.5L15 6.8Z" />
      <path d="M9 4.5v12.7" />
      <path d="M15 6.8v12.7" />
    </>
  ),
  menu: (
    <>
      <path d="M4 7h16" />
      <path d="M4 12h16" />
      <path d="M4 17h16" />
    </>
  ),
  moon: <path d="M20 14.6A8.6 8.6 0 0 1 9.4 4 8.6 8.6 0 1 0 20 14.6Z" />,
  overview: (
    <>
      <rect x="3.5" y="3.5" width="7.5" height="6" rx="1.2" />
      <rect x="13" y="3.5" width="7.5" height="10" rx="1.2" />
      <rect x="3.5" y="11.5" width="7.5" height="9" rx="1.2" />
      <rect x="13" y="15.5" width="7.5" height="5" rx="1.2" />
    </>
  ),
  payroll: (
    <>
      <rect x="3" y="6.5" width="18" height="11" rx="1.5" />
      <circle cx="12" cy="12" r="2.6" />
      <path d="M6.4 12h.01" />
      <path d="M17.6 12h.01" />
    </>
  ),
  plus: (
    <>
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </>
  ),
  merge: (
    <>
      <path d="M7 4v5.5a4 4 0 0 0 4 4h6" />
      <path d="M17 4v5.5a4 4 0 0 1-4 4H7" />
      <path d="M14 10.5l3 3-3 3" />
    </>
  ),
  upload: (
    <>
      <path d="M12 16V4" />
      <path d="M7.5 8.5 12 4l4.5 4.5" />
      <path d="M4.5 15v3.5a1.5 1.5 0 0 0 1.5 1.5h12a1.5 1.5 0 0 0 1.5-1.5V15" />
    </>
  ),
  pencil: (
    <>
      <path d="M4.5 19.5h3.5L19 8.5a2.12 2.12 0 0 0-3-3L4.5 16Z" />
      <path d="M14.6 6.6l3 3" />
    </>
  ),
  pipeline: <path d="M3.5 5h17l-6.6 7.6V20.5l-3.8-2.4v-5.5Z" />,
  pricebook: (
    <>
      <path d="M4.5 11.6 11.6 4.5H19.5V12.4L12.4 19.5Z" />
      <circle cx="15.7" cy="8.3" r="1.3" />
    </>
  ),
  properties: (
    <>
      <path d="M4.5 20V7.6L12 4l7.5 3.6V20" />
      <path d="M9.6 20v-5.2h4.8V20" />
      <path d="M8.6 11h2" />
      <path d="M13.4 11h2" />
    </>
  ),
  proposals: (
    <>
      <path d="M6 3.5h7l5 5V20.5H6Z" />
      <path d="M13 3.5V9h5" />
      <path d="M9 13h6" />
      <path d="M9 16.5h4" />
    </>
  ),
  reports: (
    <>
      <path d="M4 20h16" />
      <path d="M7.5 20v-6" />
      <path d="M12 20V7" />
      <path d="M16.5 20v-9.5" />
    </>
  ),
  search: (
    <>
      <circle cx="10.5" cy="10.5" r="6" />
      <path d="M14.9 14.9 19.5 19.5" />
    </>
  ),
  settings: (
    <>
      <path d="M4 7h9" />
      <path d="M17.5 7H20" />
      <path d="M4 12h2.5" />
      <path d="M11 12h9" />
      <path d="M4 17h7.5" />
      <path d="M16 17h4" />
      <circle cx="15.2" cy="7" r="2.2" />
      <circle cx="8.8" cy="12" r="2.2" />
      <circle cx="13.8" cy="17" r="2.2" />
    </>
  ),
  'sign-out': (
    <>
      <path d="M9.5 4.5H5.5A1.5 1.5 0 0 0 4 6v12a1.5 1.5 0 0 0 1.5 1.5h4" />
      <path d="M15.5 8l4 4-4 4" />
      <path d="M19.5 12H9.5" />
    </>
  ),
  sun: (
    <>
      <circle cx="12" cy="12" r="4.2" />
      <path d="M12 2.8v2.2" />
      <path d="M12 19v2.2" />
      <path d="M2.8 12h2.2" />
      <path d="M19 12h2.2" />
      <path d="M5.5 5.5 7 7" />
      <path d="M17 17l1.5 1.5" />
      <path d="M18.5 5.5 17 7" />
      <path d="M7 17l-1.5 1.5" />
    </>
  ),
  'system-theme': (
    <>
      <rect x="3" y="5" width="18" height="12" rx="1.5" />
      <path d="M9 20.5h6" />
      <path d="M12 17v3.5" />
    </>
  ),
  team: (
    <>
      <rect x="4" y="4.5" width="16" height="15" rx="2" />
      <circle cx="12" cy="10" r="2.6" />
      <path d="M8 17.5a4 4 0 0 1 8 0" />
    </>
  ),
  trash: (
    <>
      <path d="M4.5 7h15" />
      <path d="M9.5 7V4.5h5V7" />
      <path d="M6.6 7l1 12.5h8.8L17.4 7" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8.5" r="3.5" />
      <path d="M5 20a7 7 0 0 1 14 0" />
    </>
  ),
};

/** Every glyph name, for catalogue pages and exhaustiveness tests. */
export const ICON_NAMES = Object.keys(GLYPHS).sort() as IconName[];

export interface IconProps {
  /** Which glyph to draw. */
  readonly name: IconName;
  /**
   * An accessible name.
   *
   * Omit it — the common case — and the icon is hidden from assistive
   * technology, which is correct whenever a visible label sits beside it.
   * Supply it only when the icon is the *only* thing conveying the meaning.
   */
  readonly label?: string;
  /** Edge length, in any CSS length unit. Defaults to 1.25em so it tracks text. */
  readonly size?: string;
  /** Extra class names for layout. */
  readonly className?: string;
}

/**
 * Draws one glyph from the set.
 *
 * @param props see {@link IconProps}
 * @returns an inline SVG element
 */
export function Icon({ name, label, size = '1.25em', className }: IconProps): React.JSX.Element {
  const decorative = label === undefined;

  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      role={decorative ? 'presentation' : 'img'}
      aria-hidden={decorative ? true : undefined}
      aria-label={label}
      focusable="false"
    >
      {GLYPHS[name]}
    </svg>
  );
}
